import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const TRUCK_IMG = "https://images.unsplash.com/photo-1774929108280-652f82f21d9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxsb2dpc3RpY3MlMjBmcmVpZ2h0JTIwZ2xvYmFsfGVufDB8fHx8MTc3NzgzNzM0Nnww&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  "Air Freight", "Sea Freight (FCL)", "Sea Freight (LCL)",
  "Project Cargo", "Oversize & Heavy Lift", "Road Transport",
  "Customs Brokerage", "Warehousing", "Oil & Gas Logistics",
];

const EMP_OPTIONS = ["1-10", "11-50", "51-200", "201-500", "500+"];
const TURNOVER_OPTIONS = ["< €1M", "€1M - €5M", "€5M - €20M", "€20M - €100M", "> €100M"];

export default function ApplyPage() {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    applicant_name: "",
    email: "",
    country: "",
    city: "",
    website: "",
    services: [],
    years_in_business: 0,
    employee_count: "",
    annual_turnover: "",
    references: ["", "", ""],
    trade_lanes: "",
    about: "",
  });

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const canNext1 = form.company_name && form.applicant_name && form.email && form.country;
  const canNext2 = form.services.length > 0 && form.trade_lanes;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_in_business: Number(form.years_in_business) || 0,
        references: form.references.filter(Boolean),
      };
      await api.post("/applications", payload);
      setSubmitted(true);
      toast.success("Application received — our team will review it shortly.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="apply-success">
        <CheckCircle size={56} weight="duotone" className="text-ember mx-auto" />
        <h1 className="font-display text-4xl sm:text-5xl font-black text-navy mt-6 tracking-tighter">Application received.</h1>
        <p className="mt-4 text-navy/70 leading-relaxed">
          Thank you. Our agent layer is already pre-qualifying your application. A human operator from the
          EPAS team in Koper will be in touch within 5 business days.
        </p>
        <Link to="/" className="btn-secondary mt-8 inline-flex" data-testid="apply-back-home">Back to home</Link>
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 lg:grid-cols-12 min-h-[80vh]" data-testid="apply-page">
      <aside className="relative lg:col-span-5 min-h-[260px] lg:min-h-full bg-navy overflow-hidden">
        <img src={TRUCK_IMG} alt="Logistics" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative h-full p-10 lg:p-14 flex flex-col justify-between text-white">
          <div>
            <div className="overline !text-ember">Membership application</div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl font-black tracking-tighter">Join a curated network of independent forwarders.</h1>
            <p className="mt-6 text-white/70 leading-relaxed max-w-sm">
              Four quick steps. AI pre-qualifies your fit instantly; a human operator reviews within days.
            </p>
          </div>
          <div className="hidden lg:block mt-10 space-y-3">
            {[
              "Company profile",
              "Services & trade lanes",
              "References",
              "Review & submit",
            ].map((t, i) => (
              <div key={t} className={`flex items-center gap-3 text-sm ${step === i + 1 ? "text-ember" : "text-white/60"}`}>
                <span className={`ship-number w-6 text-right font-bold ${step === i + 1 ? "text-ember" : "text-white/40"}`}>{String(i + 1).padStart(2, "0")}</span>
                {t}
              </div>
            ))}
          </div>
        </div>
      </aside>

      <div className="lg:col-span-7 p-8 sm:p-14 bg-white">
        <div className="max-w-xl">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-navy/50 mb-8">
            Step {step} of 4
          </div>

          {step === 1 && (
            <div className="space-y-5" data-testid="apply-step-1">
              <h2 className="font-display text-3xl font-bold text-navy">Company profile</h2>
              <Field label="Company name">
                <input data-testid="apply-input-company" value={form.company_name} onChange={(e) => up("company_name", e.target.value)} className={input} />
              </Field>
              <Field label="Your name">
                <input data-testid="apply-input-applicant" value={form.applicant_name} onChange={(e) => up("applicant_name", e.target.value)} className={input} />
              </Field>
              <Field label="Work email">
                <input data-testid="apply-input-email" type="email" value={form.email} onChange={(e) => up("email", e.target.value)} className={input} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Country">
                  <input data-testid="apply-input-country" value={form.country} onChange={(e) => up("country", e.target.value)} className={input} />
                </Field>
                <Field label="City">
                  <input data-testid="apply-input-city" value={form.city} onChange={(e) => up("city", e.target.value)} className={input} />
                </Field>
              </div>
              <Field label="Company website">
                <input data-testid="apply-input-website" value={form.website} onChange={(e) => up("website", e.target.value)} className={input} placeholder="https://" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="apply-step-2">
              <h2 className="font-display text-3xl font-bold text-navy">Services & trade lanes</h2>
              <div>
                <label className="overline mb-3 block">Services offered</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`apply-service-${s.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}
                      onClick={() => toggleService(s)}
                      className={`text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                        form.services.includes(s)
                          ? "bg-navy text-white border-navy"
                          : "bg-white text-navy/70 border-black/15 hover:border-navy"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <Field label="Primary trade lanes (e.g. Adriatic — Middle East, USA — CEE)">
                <textarea data-testid="apply-input-lanes" rows={3} value={form.trade_lanes} onChange={(e) => up("trade_lanes", e.target.value)} className={input} />
              </Field>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Years in business">
                  <input data-testid="apply-input-years" type="number" min="0" value={form.years_in_business} onChange={(e) => up("years_in_business", e.target.value)} className={input} />
                </Field>
                <Field label="Employees">
                  <select data-testid="apply-input-employees" value={form.employee_count} onChange={(e) => up("employee_count", e.target.value)} className={input}>
                    <option value="">—</option>
                    {EMP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Annual turnover">
                  <select data-testid="apply-input-turnover" value={form.annual_turnover} onChange={(e) => up("annual_turnover", e.target.value)} className={input}>
                    <option value="">—</option>
                    {TURNOVER_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="apply-step-3">
              <h2 className="font-display text-3xl font-bold text-navy">References</h2>
              <p className="text-sm text-navy/60">Up to 3 references — industry partners, carriers or existing networks you cooperate with.</p>
              {form.references.map((r, i) => (
                <Field key={i} label={`Reference ${i + 1}`}>
                  <input
                    data-testid={`apply-input-ref-${i}`}
                    value={r}
                    onChange={(e) => {
                      const next = [...form.references];
                      next[i] = e.target.value;
                      up("references", next);
                    }}
                    placeholder="Company & contact"
                    className={input}
                  />
                </Field>
              ))}
              <Field label="Tell us about your company">
                <textarea data-testid="apply-input-about" rows={5} value={form.about} onChange={(e) => up("about", e.target.value)} className={input} placeholder="Positioning, specialties, what makes you a strong EPAS partner…" />
              </Field>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="apply-step-4">
              <h2 className="font-display text-3xl font-bold text-navy">Review & submit</h2>
              <div className="border border-black/10 divide-y divide-black/10 text-sm">
                <Row k="Company" v={form.company_name} />
                <Row k="Applicant" v={`${form.applicant_name} · ${form.email}`} />
                <Row k="Location" v={`${form.city ? form.city + ", " : ""}${form.country}`} />
                <Row k="Website" v={form.website || "—"} />
                <Row k="Services" v={form.services.join(", ") || "—"} />
                <Row k="Trade lanes" v={form.trade_lanes || "—"} />
                <Row k="Size" v={`${form.employee_count || "—"} employees · ${form.annual_turnover || "—"}`} />
                <Row k="References" v={form.references.filter(Boolean).join(" · ") || "—"} />
              </div>
            </div>
          )}

          <div className="mt-10 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              disabled={step === 1}
              data-testid="apply-prev-button"
              className="btn-secondary disabled:opacity-30 disabled:pointer-events-none"
            >
              <ArrowLeft size={14} /> Back
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
                data-testid="apply-next-button"
                className="btn-primary disabled:opacity-30 disabled:pointer-events-none"
              >
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="apply-submit-button"
                className="btn-primary bg-ember hover:bg-ember-600 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : <>Submit application <ArrowRight size={14} /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const input = "w-full border border-black/15 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-navy";

const Field = ({ label, children }) => (
  <div>
    <label className="overline mb-2 block">{label}</label>
    {children}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between px-4 py-3">
    <span className="overline">{k}</span>
    <span className="text-navy font-medium text-right ml-4 break-words max-w-[60%]">{v}</span>
  </div>
);
